import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { DownloadCloud, FileText, Upload } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import ProveedorSelect from './components/ProveedorSelect.jsx';
import ImportesPanel from './components/ImportesPanel.jsx';
import ItemsTable from './components/ItemsTable.jsx';
import OcrDrawer from './components/OcrDrawer.jsx';
import { purchaseFormActions, resetPurchaseFormState, usePurchaseForm } from '@/state/forms/purchaseFormSlice.js';
import { Q_PURCHASE, M_UPDATE_PURCHASE } from '@/graphql/purchases.gql.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const actions = purchaseFormActions();

const tipoOptions = ['Factura A', 'Factura B', 'Factura C', 'Factura M', 'Nota de Crédito', 'Nota de Débito'];
const monedaOptions = ['ARS', 'USD'];
const condicionOptions = ['Contado', 'Cuenta Corriente'];

export default function VerEditarCompra() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can('editar_compra');
  const state = usePurchaseForm(current => current);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result] = useQuery({
    query: Q_PURCHASE,
    variables: { id },
    pause: !id,
  });
  const [, updatePurchase] = useMutation(M_UPDATE_PURCHASE);

  useEffect(() => {
    resetPurchaseFormState();
  }, [id]);

  useEffect(() => {
    const purchase = result.data?.purchase;
    if (!purchase) {
      return;
    }
    if (purchase.supplier) {
      actions.setSupplier(purchase.supplier);
    }
    actions.setComprobanteField('tipo', purchase.tipo);
    actions.setComprobanteField('puntoVenta', purchase.puntoVenta);
    actions.setComprobanteField('numero', purchase.numero);
    actions.setComprobanteField('fecha', purchase.fecha);
    actions.setComprobanteField('cae', purchase.cae || '');
    actions.setComprobanteField('caeVto', purchase.caeVto || '');
    actions.setComprobanteField('moneda', purchase.moneda || 'ARS');
    actions.setComprobanteField('condicionCompra', purchase.condicionCompra || 'Contado');
    actions.setObservaciones(purchase.observaciones || '');

    const importes = purchase.importes || {};
    Object.entries(importes).forEach(([key, value]) => {
      if (key === 'total') {
        actions.setTotal(value);
      } else {
        actions.setImporte(key, value);
      }
    });

    if (purchase.items?.length) {
      actions.setItems(purchase.items);
    }
    if (purchase.adjuntos?.length) {
      actions.setAttachments(
        purchase.adjuntos.map(file => ({
          id: file.id,
          name: file.filename,
          size: file.size,
          type: file.mimeType,
          url: file.url,
        })),
      );
    }
  }, [result.data]);

  const attachmentsFiles = useMemo(() => state.attachments.map(item => item.file).filter(Boolean), [state.attachments]);

  const handleSubmit = async () => {
    if (!canEdit) {
      setSubmitError('No tenés permisos para editar esta compra.');
      return;
    }
    setSubmitError(null);
    if (!state.supplier?.id) {
      setSubmitError('Seleccioná un proveedor registrado para guardar la compra.');
      return;
    }
    const payload = {
      id,
      input: {
        supplierId: state.supplier.id,
        tipo: state.comprobante.tipo,
        puntoVenta: state.comprobante.puntoVenta,
        numero: state.comprobante.numero,
        fecha: state.comprobante.fecha,
        cae: state.comprobante.cae,
        caeVto: state.comprobante.caeVto,
        moneda: state.comprobante.moneda,
        condicionCompra: state.comprobante.condicionCompra,
        importes: {
          neto: Number(state.importes.neto) || 0,
          iva21: Number(state.importes.iva21) || 0,
          iva105: Number(state.importes.iva105) || 0,
          iva27: Number(state.importes.iva27) || 0,
          exento: Number(state.importes.exento) || 0,
          noGravado: Number(state.importes.noGravado) || 0,
          percepIva: Number(state.importes.percepIva) || 0,
          percepIibb: Number(state.importes.percepIibb) || 0,
          otros: Number(state.importes.otros) || 0,
          total: Number(state.importes.total) || 0,
        },
        items: state.items.map(item => ({
          id: item.id,
          codigo: item.codigo,
          descripcion: item.descripcion,
          cantidad: Number(item.cantidad) || 0,
          precio: Number(item.precio) || 0,
          alicuotaIva: Number(item.alicuotaIva) || 0,
          subtotal: Number(item.subtotal) || 0,
        })),
        adjuntos: state.attachments.map(file => ({
          id: file.id,
          filename: file.name,
          mimeType: file.type,
          size: file.size,
          url: file.url,
        })),
        observaciones: state.comprobante.observaciones,
      },
    };
    const response = await updatePurchase(payload);
    if (response.error) {
      setSubmitError(response.error.message || 'No pudimos actualizar la compra.');
      return;
    }
    navigate('/tesoreria/compras', { replace: true });
  };

  const handleFileChange = event => {
    const files = Array.from(event.target.files || []);
    if (files.length) {
      actions.addAttachments(files);
    }
    event.target.value = '';
  };

  const handleRemoveAttachment = idToRemove => {
    actions.removeAttachment(idToRemove);
  };

  if (result.fetching && !result.data) {
    return (
      <div className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.6)] px-6 py-20 text-center text-sm text-[rgba(var(--muted),0.85)]">
        Cargando compra...
      </div>
    );
  }

  if (result.error) {
    return (
      <div className="rounded-2xl border border-red-300 bg-red-50 px-6 py-8 text-center text-sm text-red-700">
        No se pudo cargar la compra: {result.error.message}
      </div>
    );
  }

  if (!result.data?.purchase) {
    return (
      <div className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.6)] px-6 py-8 text-center text-sm text-[rgba(var(--muted),0.85)]">
        La compra solicitada no existe o ya fue eliminada.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={canEdit ? 'Editar compra' : 'Detalle de compra'}
        description="Actualizá información del comprobante o ajustá importes cuando sea necesario."
        breadcrumbs={[
          { label: 'Tesorería', href: '/tesoreria' },
          { label: 'Registro de compras', href: '/tesoreria/compras' },
          { label: canEdit ? 'Editar compra' : 'Detalle de compra' },
        ]}
        actions={canEdit ? (
          <button
            type="button"
            onClick={() => setOcrOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-[rgba(var(--brand),0.12)] px-4 py-2 text-sm font-semibold text-[rgb(var(--brand))] shadow-[0_12px_22px_rgba(var(--shadow),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            <DownloadCloud className="h-4 w-4" aria-hidden="true" />
            Leer con OCR
          </button>
        ) : null}
      />

      {!canEdit ? (
        <div className="rounded-2xl border border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.1)] px-4 py-3 text-sm text-[rgb(var(--brand-700))]">
          Estás viendo esta compra en modo solo lectura.
        </div>
      ) : null}

      <fieldset disabled={!canEdit} className="contents disabled:opacity-75">
      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <ProveedorSelect />
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Datos del comprobante</h2>
        <div className="mt-4 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-3">
            <Field label="Tipo">
              <select
                value={state.comprobante.tipo}
                onChange={event => actions.setComprobanteField('tipo', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {tipoOptions.map(option => (
                  <option key={option} value={option} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                    {option}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Punto de venta">
              <input
                type="text"
                value={state.comprobante.puntoVenta}
                onChange={event => actions.setComprobanteField('puntoVenta', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Número">
              <input
                type="text"
                value={state.comprobante.numero}
                onChange={event => actions.setComprobanteField('numero', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Fecha">
              <input
                type="date"
                value={state.comprobante.fecha}
                onChange={event => actions.setComprobanteField('fecha', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="CAE">
              <input
                type="text"
                value={state.comprobante.cae}
                onChange={event => actions.setComprobanteField('cae', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Vencimiento CAE">
              <input
                type="date"
                value={state.comprobante.caeVto}
                onChange={event => actions.setComprobanteField('caeVto', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Moneda">
              <select
                value={state.comprobante.moneda}
                onChange={event => actions.setComprobanteField('moneda', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {monedaOptions.map(option => (
                  <option key={option} value={option} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                    {option}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label="Condición de compra">
              <select
                value={state.comprobante.condicionCompra}
                onChange={event => actions.setComprobanteField('condicionCompra', event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {condicionOptions.map(option => (
                  <option key={option} value={option} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                    {option}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12">
            <Field label="Observaciones">
              <textarea
                rows={3}
                value={state.comprobante.observaciones}
                onChange={event => actions.setObservaciones(event.target.value)}
                className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-3 text-sm text-[rgb(var(--fg))] shadow-inner focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
        </div>
      </section>

      <ImportesPanel />

      <ItemsTable />

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Adjuntos</h2>
        <p className="mt-1 text-sm text-[rgba(var(--muted),0.85)]">Subí nuevos archivos si necesitás reemplazar los existentes.</p>
        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center">
          <label className="flex items-center gap-3 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.55)] px-4 py-3 text-sm font-semibold text-[rgb(var(--fg))] shadow-[0_12px_22px_rgba(var(--shadow),0.1)] focus-within:ring-2 focus-within:ring-[rgb(var(--ring))]">
            <Upload className="h-4 w-4 text-[rgb(var(--brand))]" aria-hidden="true" />
            <span>Agregar archivos</span>
            <input
              type="file"
              multiple
              accept="application/pdf,image/*"
              onChange={handleFileChange}
              className="sr-only"
            />
          </label>
          {attachmentsFiles.length ? (
            <button
              type="button"
              onClick={() => setOcrOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.12)] px-4 py-2 text-sm font-semibold text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              Usar estos archivos en OCR
            </button>
          ) : null}
        </div>
        {state.attachments.length ? (
          <ul className="mt-4 space-y-3">
            {state.attachments.map(file => (
              <li
                key={file.id}
                className="flex items-center justify-between rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.5)] px-4 py-3 text-sm text-[rgb(var(--fg))]"
              >
                <div>
                  <p className="font-semibold">{file.name}</p>
                  <p className="text-xs text-[rgba(var(--muted),0.8)]">{file.size ? (file.size / 1024).toFixed(1) : '—'} KB</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(file.id)}
                  className="rounded-xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.6)] px-3 py-1 text-xs font-semibold text-[rgba(var(--muted),0.85)] hover:text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-sm text-[rgba(var(--muted),0.8)]">Aún no hay archivos asociados.</p>
        )}
      </section>

      </fieldset>

      {submitError ? (
        <div className="rounded-2xl border border-[rgba(var(--brand),0.45)] bg-[rgba(var(--brand),0.1)] px-4 py-3 text-sm text-[rgb(var(--brand-700))]">
          {submitError}
        </div>
      ) : null}

      <StickyActions
        onCancel={() => navigate('/tesoreria/compras')}
        onSubmit={handleSubmit}
        primaryLabel={canEdit ? 'Guardar cambios' : 'Edición no habilitada'}
        secondaryLabel={canEdit ? 'Cancelar' : 'Volver al listado'}
        disablePrimary={!canEdit}
      />

      <OcrDrawer isOpen={ocrOpen} onClose={() => setOcrOpen(false)} files={attachmentsFiles} />
    </div>
  );
}
